import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/lib/icons";
import type { BackendPayoutRequest, PayoutRequestStatus } from "@/lib/types/payments";
import { cn } from "@/lib/utils";
import { GetPaidCard, GetPaidEmptyState, GetPaidSectionTitle } from "./GetPaidPrimitives";

type StatusDisplay = {
  label: ReactNode;
  className: string;
};

const statusClassName: Record<PayoutRequestStatus, string> = {
  PENDING: "border-brand/20 bg-brand/[0.06] text-brand",
  APPROVED: "border-success/20 bg-success/[0.06] text-success",
  REJECTED: "border-danger/20 bg-danger/[0.06] text-danger",
  PAID: "border-info/[0.18] bg-info/[0.06] text-info",
};

export function PayoutRequestHistory({
  title,
  requests,
  emptyTitle,
  emptyHint,
  labels,
  formatAmount,
  formatDate,
  getPhone,
  onCreateRequest,
  isEligible,
  createRequestLabel,
}: {
  title: ReactNode;
  requests: BackendPayoutRequest[];
  emptyTitle: ReactNode;
  emptyHint?: ReactNode;
  labels: Record<PayoutRequestStatus, ReactNode> & {
    requestedOn: ReactNode;
    moderatorNote: ReactNode;
    refundedTitle: ReactNode;
    refundedDescription: (amount: string) => ReactNode;
    transactionReference: (reference: string) => ReactNode;
    paidOn: (date: string) => ReactNode;
    pendingInfo: ReactNode;
    approvedInfo: ReactNode;
  };
  formatAmount: (amount: number) => string;
  formatDate: (date: string) => string;
  getPhone: (request: BackendPayoutRequest) => string;
  onCreateRequest?: () => void;
  isEligible?: boolean;
  createRequestLabel?: ReactNode;
}) {
  const getStatus = (status: PayoutRequestStatus): StatusDisplay => ({
    label: labels[status] ?? labels.PENDING,
    className: statusClassName[status] ?? statusClassName.PENDING,
  });

  return (
    <GetPaidCard>
      <div className="flex items-center justify-between gap-4">
        <GetPaidSectionTitle title={title} />
        {onCreateRequest && isEligible && (
          <Button 
            onClick={onCreateRequest} 
            className="shrink-0 gap-2"
            size="sm"
          >
            <Icon name="plus" className="h-4 w-4" />
            {createRequestLabel || "Créer demande"}
          </Button>
        )}
      </div>
      {requests.length === 0 ? (
        <GetPaidEmptyState>
          <p>{emptyTitle}</p>
          {emptyHint && <p className="mt-2 text-xs">{emptyHint}</p>}
        </GetPaidEmptyState>
      ) : (
        <div className="rounded-lg border divide-y divide-border/40 border-border/40">
          {requests.map((request) => {
            const status = getStatus(request.status);
            const phone = getPhone(request);

            return (
              <article key={request.id} className="py-4 transition-colors hover:bg-slate-900/[0.015] dark:hover:bg-white/[0.015] sm:px-2">
                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                  <div className="min-w-0 space-y-2">
                    <Badge className={cn("w-fit rounded-full border px-2.5 py-1 text-[0.64rem] font-semibold uppercase tracking-[0.14em] shadow-none", status.className)}>
                      {status.label}
                    </Badge>
                    <p className="text-sm text-muted-foreground/74">
                      {labels.requestedOn} {formatDate(request.createdAt)}
                    </p>
                    {request.description && <p className="text-xs leading-5 text-muted-foreground/66">{request.description}</p>}
                  </div>

                  <div className="shrink-0 sm:text-right">
                    <p className="text-xl font-semibold tracking-tight text-success">{formatAmount(request.amount)}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5 sm:justify-end">
                      <Badge variant="outline" className="rounded-full border-border/40 bg-transparent text-xs text-muted-foreground/74">
                        {phone || "0"}
                      </Badge>
                      <Badge variant="outline" className="rounded-full border-border/40 bg-transparent text-xs text-muted-foreground/74">
                        {request.network}
                      </Badge>
                    </div>
                  </div>
                </div>

                {(request.moderatorNote || request.status === "REJECTED" || request.txReference || request.paidAt || request.status === "PENDING" || request.status === "APPROVED") && (
                  <div className="mt-4 space-y-2 border-t border-border/40 pt-3 text-xs leading-5 text-muted-foreground/70">
                    {request.moderatorNote && (
                      <div>
                        <p className="font-semibold text-foreground/76">{labels.moderatorNote}</p>
                        <p>{request.moderatorNote}</p>
                      </div>
                    )}
                    {request.status === "REJECTED" && (
                      <div className="text-brand">
                        <p className="font-semibold">{labels.refundedTitle}</p>
                        <p>{labels.refundedDescription(formatAmount(request.amount))}</p>
                      </div>
                    )}
                    {request.txReference && <p>{labels.transactionReference(request.txReference)}</p>}
                    {request.paidAt && <p className="text-success">{labels.paidOn(formatDate(request.paidAt))}</p>}
                    {request.status === "PENDING" && (
                      <p className="flex items-center gap-2">
                        <Icon name="info" className="h-4 w-4" />
                        {labels.pendingInfo}
                      </p>
                    )}
                    {request.status === "APPROVED" && (
                      <p className="flex items-center gap-2 text-success">
                        <Icon name="checkCircle" className="h-4 w-4" />
                        {labels.approvedInfo}
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </GetPaidCard>
  );
}
